import { z } from 'zod';

import type { SearchExperimentLayer } from '@atlassian/search-experiment';

import { PageStatusFilterValueKey } from '../filters/page-status-filter';
import { TypeFilterValueKey } from '../filters/universal-type';
import {
	type ProductKeys as ProductKeysFromProducts,
	ProductKeys as ProductKeysMap,
} from '../products';

import { isForgeConnectorProductKey } from './3p-config';

// Do not use z.coerce.boolean because JS type coercion from string to boolean is not logical
export const booleanSchema: z.ZodEffects<
	z.ZodEnum<['true', 'false']>,
	boolean,
	'true' | 'false'
> = z.enum(['true', 'false']).transform((x) => x === 'true');
export const commaSeparatedArraySchema: z.ZodEffects<z.ZodString, string[], string> = z
	.string()
	.transform((x) => x.split(','));

// Schema that accepts both enum values and dynamic Forge connectors
export const productKeySchema: z.ZodUnion<
	[
		z.ZodNativeEnum<{
			readonly AtlassianWebCrawler: 'atlassian-web-crawler';
			readonly Figma: 'figma';
			readonly Github: 'github';
			readonly GoogleDrive: 'drive';
			readonly Loom: 'loom';
			readonly OneDrive: 'onedrive';
			readonly Dropbox: 'dropbox';
			readonly Slack: 'slack';
			readonly Sharepoint: 'sharepoint';
			readonly Teams: 'teams';
			readonly GoogleCalendar: 'google-calendar';
			readonly OutlookCalendar: 'outlook-calendar';
			readonly OutlookMail: 'outlook-mail';
			readonly Gmail: 'gmail';
			readonly AdobeSign: 'adobe-sign';
			readonly Asana: 'asana';
			readonly Zendesk: 'zendesk';
			readonly Airtable: 'airtable';
			readonly Smartsheet: 'smartsheet';
			readonly Notion: 'notion';
			readonly Miro: 'miro';
			readonly Docusign: 'docusign';
			readonly Lucid: 'lucid';
			readonly Monday: 'monday';
			readonly AzureDevops: 'azure-devops';
			readonly Gitlab: 'gitlab';
			readonly ConfluenceDC: 'confluence-dc';
			readonly Canva: 'canva';
			readonly Box: 'box';
			readonly Zeplin: 'zeplin';
			readonly ServiceNow: 'servicenow';
			readonly Freshservice: 'freshservice';
			readonly PowerBI: 'power-bi';
			readonly ClickUp: 'clickup';
			readonly Amplitude: 'amplitude';
			readonly Stripe: 'stripe';
			readonly AdobeXD: 'adobexd';
			readonly Aha: 'aha';
			readonly Mural: 'mural';
			readonly Webex: 'webex';
			readonly Todoist: 'todoist';
			readonly PagerDuty: 'pagerduty';
			readonly Pipedrive: 'pipedrive';
			readonly Hubspot: 'hubspot';
			readonly Dovetail: 'dovetail';
			readonly LaunchDarkly: 'launchdarkly';
			readonly Salesforce: 'salesforce';
			readonly Sentry: 'sentry';
			readonly JiraDC: 'jira-dc';
			readonly Gitbook: 'gitbook';
			readonly AWSS3: 'aws-s3';
			readonly Databricks: 'databricks';
			readonly JsmDC: 'jsm-dc';
			readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
			readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
			readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
			readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
			readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
			readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
			readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
			readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
			readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
			readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
			readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
			readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
			readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
			readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
			readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
			readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
			readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
			readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
			readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
			readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
			readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
			readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
			readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
			readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
			readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
			readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
			readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
			readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
			readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
			readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
			readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
			readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
			readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
			readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
			readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
			readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
			readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
			readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
			readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
			readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
			readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
			readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
			readonly Atlas: 'townsquare';
			readonly Bitbucket: 'bitbucket';
			readonly Compass: 'compass';
			readonly Confluence: 'confluence';
			readonly Jira: 'jira';
			readonly JiraProductDiscovery: 'jira-product-discovery';
			readonly JiraServiceManagement: 'jira-servicedesk';
			readonly Trello: 'trello';
			readonly Focus: 'mercury';
			readonly Assets: 'cmdb';
			readonly Talent: 'radar';
		}>,
		z.ZodEffects<z.ZodString, string, string>,
	]
> = z.union([
	z.nativeEnum(ProductKeysMap),
	z.string().refine(isForgeConnectorProductKey, (val) => ({
		message: `${val} is not a valid product key`,
	})),
]);
// Use the same ProductKeys type as products.tsx to ensure type compatibility
// This avoids needing casts everywhere - both types are now identical
export type ProductKeys = ProductKeysFromProducts;
export const ProductKey: {
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly GoogleDrive: 'drive';
	readonly Loom: 'loom';
	readonly OneDrive: 'onedrive';
	readonly Dropbox: 'dropbox';
	readonly Slack: 'slack';
	readonly Sharepoint: 'sharepoint';
	readonly Teams: 'teams';
	readonly GoogleCalendar: 'google-calendar';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly OutlookMail: 'outlook-mail';
	readonly Gmail: 'gmail';
	readonly AdobeSign: 'adobe-sign';
	readonly Asana: 'asana';
	readonly Zendesk: 'zendesk';
	readonly Airtable: 'airtable';
	readonly Smartsheet: 'smartsheet';
	readonly Notion: 'notion';
	readonly Miro: 'miro';
	readonly Docusign: 'docusign';
	readonly Lucid: 'lucid';
	readonly Monday: 'monday';
	readonly AzureDevops: 'azure-devops';
	readonly Gitlab: 'gitlab';
	readonly ConfluenceDC: 'confluence-dc';
	readonly Canva: 'canva';
	readonly Box: 'box';
	readonly Zeplin: 'zeplin';
	readonly ServiceNow: 'servicenow';
	readonly Freshservice: 'freshservice';
	readonly PowerBI: 'power-bi';
	readonly ClickUp: 'clickup';
	readonly Amplitude: 'amplitude';
	readonly Stripe: 'stripe';
	readonly AdobeXD: 'adobexd';
	readonly Aha: 'aha';
	readonly Mural: 'mural';
	readonly Webex: 'webex';
	readonly Todoist: 'todoist';
	readonly PagerDuty: 'pagerduty';
	readonly Pipedrive: 'pipedrive';
	readonly Hubspot: 'hubspot';
	readonly Dovetail: 'dovetail';
	readonly LaunchDarkly: 'launchdarkly';
	readonly Salesforce: 'salesforce';
	readonly Sentry: 'sentry';
	readonly JiraDC: 'jira-dc';
	readonly Gitbook: 'gitbook';
	readonly AWSS3: 'aws-s3';
	readonly Databricks: 'databricks';
	readonly JsmDC: 'jsm-dc';
	readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
	readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
	readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
	readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
	readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
	readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
	readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
	readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
	readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
	readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
	readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
	readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
	readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
	readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
	readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
	readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
	readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
	readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
	readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
	readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
	readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
	readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
	readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
	readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
	readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
	readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
	readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
	readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
	readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
	readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
	readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
	readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
	readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
	readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
	readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
	readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
	readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
	readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
	readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
	readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
	readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
	readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
	readonly Atlas: 'townsquare';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Trello: 'trello';
	readonly Focus: 'mercury';
	readonly Assets: 'cmdb';
	readonly Talent: 'radar';
} = z.nativeEnum(ProductKeysMap).enum;

export const allContentTypeSchema: z.ZodNativeEnum<{
	All: string;
}> = z.nativeEnum({
	All: 'all',
});
export type AllContentTypes = z.infer<typeof allContentTypeSchema>;
export const AllContentType: {
	All: string;
} = allContentTypeSchema.enum;

export const atlasContentTypeSchema: z.ZodNativeEnum<{
	readonly Goal: 'goal';
	readonly Learning: 'learning';
	readonly Project: 'project';
	readonly Tag: 'tag';
}> = z.nativeEnum({
	Goal: 'goal',
	Learning: 'learning',
	Project: 'project',
	Tag: 'tag',
} as const);
export type AtlasContentTypes = z.infer<typeof atlasContentTypeSchema>;
export const AtlasContentType: {
	readonly Goal: 'goal';
	readonly Learning: 'learning';
	readonly Project: 'project';
	readonly Tag: 'tag';
} = atlasContentTypeSchema.enum;

export const asanaContentTypeSchema: z.ZodNativeEnum<{
	readonly WorkItem: 'work-item';
}> = z.nativeEnum({
	WorkItem: 'work-item',
} as const);

export type AsanaContentType = z.infer<typeof asanaContentTypeSchema>;
export const AsanaContentType: {
	readonly WorkItem: 'work-item';
} = asanaContentTypeSchema.enum;

export const confluenceContentTypeSchema: z.ZodNativeEnum<{
	readonly Answer: 'answer';
	readonly Attachment: 'attachment';
	readonly Blogpost: 'blogpost';
	readonly Calendar: 'calendar';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Document: 'document';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Gliffy: 'gliffy';
	readonly Page: 'page';
	readonly Question: 'question';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Whiteboard: 'whiteboard';
}> = z.nativeEnum({
	Answer: 'answer',
	Attachment: 'attachment',
	Blogpost: 'blogpost',
	Calendar: 'calendar',
	Comment: 'comment',
	Database: 'database',
	Document: 'document',
	Embed: 'embed',
	Folder: 'folder',
	Gliffy: 'gliffy',
	Page: 'page',
	Question: 'question',
	Space: 'space',
	SpaceCalendar: 'space-calendar',
	Whiteboard: 'whiteboard',
} as const);
export type ConfluenceContentTypes = z.infer<typeof confluenceContentTypeSchema>;
export const ConfluenceContentType: {
	readonly Answer: 'answer';
	readonly Attachment: 'attachment';
	readonly Blogpost: 'blogpost';
	readonly Calendar: 'calendar';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Document: 'document';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Gliffy: 'gliffy';
	readonly Page: 'page';
	readonly Question: 'question';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Whiteboard: 'whiteboard';
} = confluenceContentTypeSchema.enum;

export const googleContentTypeSchema: z.ZodNativeEnum<{
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
	readonly Video: 'video';
}> = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
	Video: 'video',
} as const);
export type GoogleContentTypes = z.infer<typeof googleContentTypeSchema>;
export const GoogleContentType: {
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
	readonly Video: 'video';
} = googleContentTypeSchema.enum;

export const microsoftContentTypeSchema: z.ZodNativeEnum<{
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
	readonly Image: 'image';
	readonly Folder: 'folder';
	readonly Audio: 'audio';
	readonly Video: 'video';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Form: 'form';
	readonly WebPage: 'web-page';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Notebook: 'notebook';
	readonly Other: 'other';
}> = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
	Image: 'image',
	Folder: 'folder',
	Audio: 'audio',
	Video: 'video',
	Pdf: 'pdf',
	Shortcut: 'shortcut',
	Code: 'code',
	Archive: 'archive',
	Form: 'form',
	WebPage: 'web-page',
	Page: 'page',
	Blogpost: 'blogpost',
	Notebook: 'notebook',
	Other: 'other',
} as const);
export type MicrosoftContentTypes = z.infer<typeof microsoftContentTypeSchema>;
export const MicrosoftContentType: {
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
	readonly Image: 'image';
	readonly Folder: 'folder';
	readonly Audio: 'audio';
	readonly Video: 'video';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Form: 'form';
	readonly WebPage: 'web-page';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Notebook: 'notebook';
	readonly Other: 'other';
} = microsoftContentTypeSchema.enum;

export const dropboxContentTypeSchema: z.ZodNativeEnum<{
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
}> = z.nativeEnum({
	Document: 'document',
	Presentation: 'presentation',
	Spreadsheet: 'spreadsheet',
} as const);
export type DropboxContentTypes = z.infer<typeof dropboxContentTypeSchema>;
export const DropboxContentType: {
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
} = dropboxContentTypeSchema.enum;

export const jiraContentTypeSchema: z.ZodNativeEnum<{
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Issue: 'issue';
	readonly Filter: 'filter';
	readonly Dashboard: 'dashboard';
	readonly Board: 'board';
	readonly Plan: 'plan';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
}> = z.nativeEnum({
	Project: 'project',
	Incident: 'incident',
	Issue: 'issue',
	Filter: 'filter',
	Dashboard: 'dashboard',
	Board: 'board',
	Plan: 'plan',
	Epic: 'epic',
	Bug: 'bug',
	Story: 'story',
	Task: 'task',
	Subtask: 'subtask',
} as const);
export type JiraContentTypes = z.infer<typeof jiraContentTypeSchema>;
export const JiraContentType: {
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Issue: 'issue';
	readonly Filter: 'filter';
	readonly Dashboard: 'dashboard';
	readonly Board: 'board';
	readonly Plan: 'plan';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
} = jiraContentTypeSchema.enum;

export const compassContentTypeSchema: z.ZodNativeEnum<{
	readonly Component: 'component';
}> = z.nativeEnum({
	Component: 'component',
} as const);
export type CompassContentTypes = z.infer<typeof compassContentTypeSchema>;
export const CompassContentType: {
	readonly Component: 'component';
} = compassContentTypeSchema.enum;

export const mercuryContentTypeSchema: z.ZodNativeEnum<{
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
}> = z.nativeEnum({
	FocusArea: 'focus-area',
	Ask: 'ask',
} as const);
export type MercuryContentTypes = z.infer<typeof mercuryContentTypeSchema>;
export const MercuryContentType: {
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
} = mercuryContentTypeSchema.enum;

export const talentContentTypeSchema: z.ZodNativeEnum<{
	readonly Position: 'position';
}> = z.nativeEnum({
	Position: 'position',
} as const);
export type TalentContentTypes = z.infer<typeof talentContentTypeSchema>;
export const TalentContentType: {
	readonly Position: 'position';
} = talentContentTypeSchema.enum;

export const trelloContentTypeSchema: z.ZodNativeEnum<{
	readonly Board: 'board';
	readonly Card: 'card';
}> = z.nativeEnum({
	Board: 'board',
	Card: 'card',
} as const);
export type TrelloContentTypes = z.infer<typeof trelloContentTypeSchema>;
export const TrelloContentType: {
	readonly Board: 'board';
	readonly Card: 'card';
} = trelloContentTypeSchema.enum;

export const slackContentTypeSchema: z.ZodNativeEnum<{
	readonly Message: 'message';
	readonly Conversation: 'conversation';
}> = z.nativeEnum({
	Message: 'message',
	Conversation: 'conversation',
} as const);
export type SlackContentTypes = z.infer<typeof slackContentTypeSchema>;
export const SlackContentType: {
	readonly Message: 'message';
	readonly Conversation: 'conversation';
} = slackContentTypeSchema.enum;

export const teamsContentTypeSchema: z.ZodNativeEnum<{
	readonly Message: 'message';
	readonly Conversation: 'conversation';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Attachment: 'attachment';
}> = z.nativeEnum({
	Message: 'message',
	Conversation: 'conversation',
	MeetingTranscripts: 'meeting-transcripts',
	Attachment: 'attachment',
} as const);
export type TeamsContentTypes = z.infer<typeof teamsContentTypeSchema>;
export const TeamsContentType: {
	readonly Message: 'message';
	readonly Conversation: 'conversation';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Attachment: 'attachment';
} = teamsContentTypeSchema.enum;
export const githubContentTypeSchema: z.ZodNativeEnum<{
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Commit: 'commit';
	readonly Branch: 'branch';
}> = z.nativeEnum({
	Repository: 'repository',
	PullRequest: 'pull-request',
	Commit: 'commit',
	Branch: 'branch',
} as const);

export type GithubContentType = z.infer<typeof githubContentTypeSchema>;
export const GithubContentType: {
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Commit: 'commit';
	readonly Branch: 'branch';
} = githubContentTypeSchema.enum;

export const webPageContentTypeSchema: z.ZodNativeEnum<{
	readonly WebPage: 'web-page';
}> = z.nativeEnum({
	WebPage: 'web-page',
} as const);
export type WebPageContentTypes = z.infer<typeof webPageContentTypeSchema>;
export const WebPageContentType: {
	readonly WebPage: 'web-page';
} = webPageContentTypeSchema.enum;

export const figmaContentTypeSchema: z.ZodNativeEnum<{
	readonly Design: 'design';
}> = z.nativeEnum({
	Design: 'design',
} as const);
export type FigmaContentTypes = z.infer<typeof figmaContentTypeSchema>;
export const FigmaContentType: {
	readonly Design: 'design';
} = figmaContentTypeSchema.enum;

export const loomContentTypeSchema: z.ZodNativeEnum<{
	readonly Video: 'video';
}> = z.nativeEnum({
	Video: 'video',
} as const);
export type LoomContentTypes = z.infer<typeof loomContentTypeSchema>;
export const LoomContentType: {
	readonly Video: 'video';
} = loomContentTypeSchema.enum;

export const zendeskContentTypeSchema: z.ZodNativeEnum<{
	readonly WorkItem: 'work-item';
	readonly Document: 'document';
}> = z.nativeEnum({
	WorkItem: 'work-item',
	Document: 'document',
} as const);
export type ZendeskContentType = z.infer<typeof zendeskContentTypeSchema>;
export const ZendeskContentType: {
	readonly WorkItem: 'work-item';
	readonly Document: 'document';
} = zendeskContentTypeSchema.enum;

export const smartLinksContentTypeSchema: z.ZodNativeEnum<{
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
}> = z.nativeEnum({
	Dashboard: 'dashboard',
	Invoice: 'invoice',
	Deal: 'deal',
} as const);
export type SmartLinksContentType = z.infer<typeof smartLinksContentTypeSchema>;
export const SmartLinksContentType: {
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
} = smartLinksContentTypeSchema.enum;

export const salesforceContentTypeSchema: z.ZodNativeEnum<{
	readonly Deal: 'deal';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
}> = z.nativeEnum({
	Deal: 'deal',
	CustomerContact: 'customer-contact',
	CustomerOrg: 'customer-org',
} as const);
export type SalesforceContentTypes = z.infer<typeof salesforceContentTypeSchema>;
export const SalesforceContentType: {
	readonly Deal: 'deal';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
} = salesforceContentTypeSchema.enum;

export const databricksContentTypeSchema: z.ZodNativeEnum<{
	readonly Dashboard: 'dashboard';
	readonly DataTable: 'data-table';
}> = z.nativeEnum({
	Dashboard: 'dashboard',
	DataTable: 'data-table',
} as const);
export type DatabricksContentTypes = z.infer<typeof databricksContentTypeSchema>;
export const DatabricksContentType: {
	readonly Dashboard: 'dashboard';
	readonly DataTable: 'data-table';
} = databricksContentTypeSchema.enum;

export const hubspotContentTypeSchema: z.ZodNativeEnum<{
	readonly Deal: 'deal';
	readonly CustomerOrg: 'customer-org';
}> = z.nativeEnum({
	Deal: 'deal',
	CustomerOrg: 'customer-org',
} as const);
export type HubspotContentTypes = z.infer<typeof hubspotContentTypeSchema>;
export const HubspotContentType: {
	readonly Deal: 'deal';
	readonly CustomerOrg: 'customer-org';
} = hubspotContentTypeSchema.enum;

export const assetsContentTypeSchema: z.ZodNativeEnum<{
	readonly AssetsObject: 'object';
	readonly Schema: 'schema';
	readonly Type: 'type';
}> = z.nativeEnum({
	AssetsObject: 'object',
	Schema: 'schema',
	Type: 'type',
} as const);
export const AssetsContentType: {
	readonly AssetsObject: 'object';
	readonly Schema: 'schema';
	readonly Type: 'type';
} = assetsContentTypeSchema.enum;
export type AssetsContentTypes = z.infer<typeof assetsContentTypeSchema>;

// this is the original enum (prior to using TypeFilterValueKey), it is only used for testing purposes for now
export const contentTypeSchemaOriginal: z.ZodNativeEnum<{
	readonly Position: 'position';
	readonly AssetsObject: 'object';
	readonly Schema: 'schema';
	readonly Type: 'type';
	readonly Dashboard: 'dashboard';
	readonly DataTable: 'data-table';
	readonly Deal: 'deal';
	readonly CustomerOrg: 'customer-org';
	readonly CustomerContact: 'customer-contact';
	readonly Invoice: 'invoice';
	readonly WorkItem: 'work-item';
	readonly Document: 'document';
	readonly Presentation: 'presentation';
	readonly Spreadsheet: 'spreadsheet';
	readonly WebPage: 'web-page';
	readonly Video: 'video';
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Commit: 'commit';
	readonly Branch: 'branch';
	readonly Design: 'design';
	readonly Message: 'message';
	readonly Conversation: 'conversation';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Attachment: 'attachment';
	readonly Board: 'board';
	readonly Card: 'card';
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
	readonly Component: 'component';
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Issue: 'issue';
	readonly Filter: 'filter';
	readonly Plan: 'plan';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
	readonly Image: 'image';
	readonly Folder: 'folder';
	readonly Audio: 'audio';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Form: 'form';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Notebook: 'notebook';
	readonly Other: 'other';
	readonly Answer: 'answer';
	readonly Calendar: 'calendar';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Embed: 'embed';
	readonly Gliffy: 'gliffy';
	readonly Question: 'question';
	readonly Space: 'space';
	readonly SpaceCalendar: 'space-calendar';
	readonly Whiteboard: 'whiteboard';
	readonly Goal: 'goal';
	readonly Learning: 'learning';
	readonly Tag: 'tag';
	readonly All: string;
}> = z.nativeEnum({
	...AllContentType,
	...AsanaContentType,
	...AtlasContentType,
	...ConfluenceContentType,
	...GoogleContentType,
	...MicrosoftContentType,
	...JiraContentType,
	...CompassContentType,
	...MercuryContentType,
	...TrelloContentType,
	...SlackContentType,
	...TeamsContentType,
	...FigmaContentType,
	...GithubContentType,
	...LoomContentType,
	...WebPageContentType,
	...DropboxContentType,
	...ZendeskContentType,
	...SmartLinksContentType,
	...SalesforceContentType,
	...HubspotContentType,
	...DatabricksContentType,
	...AssetsContentType,
	...TalentContentType,
} as const);

export const contentTypeSchema: z.ZodNativeEnum<{
	readonly All: string;
	readonly Document: 'document';
	readonly Spreadsheet: 'spreadsheet';
	readonly Presentation: 'presentation';
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly WebPage: 'web-page';
	readonly Conversation: 'conversation';
	readonly Message: 'message';
	readonly Video: 'video';
	readonly Design: 'design';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Attachment: 'attachment';
	readonly Space: 'space';
	readonly Whiteboard: 'whiteboard';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Calendar: 'calendar';
	readonly SpaceCalendar: 'space-calendar';
	readonly Question: 'question';
	readonly Answer: 'answer';
	readonly Issue: 'issue';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Filter: 'filter';
	readonly Plan: 'plan';
	readonly Board: 'board';
	readonly Component: 'component';
	readonly Card: 'card';
	readonly Goal: 'goal';
	readonly Tag: 'tag';
	readonly WorkItem: 'work-item';
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
	readonly Gliffy: 'gliffy';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
	readonly DataTable: 'data-table';
	readonly AssetsObject: 'object';
	readonly Type: 'type';
	readonly Schema: 'schema';
	readonly Position: 'position';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Image: 'image';
	readonly Audio: 'audio';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Other: 'other';
	readonly Notebook: 'notebook';
	readonly Form: 'form';
}> = z.nativeEnum({
	...TypeFilterValueKey,
	...AllContentType,
} as const);

export type ContentTypes = z.infer<typeof contentTypeSchema>;
export const ContentType: {
	readonly All: string;
	readonly Document: 'document';
	readonly Spreadsheet: 'spreadsheet';
	readonly Presentation: 'presentation';
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly WebPage: 'web-page';
	readonly Conversation: 'conversation';
	readonly Message: 'message';
	readonly Video: 'video';
	readonly Design: 'design';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Attachment: 'attachment';
	readonly Space: 'space';
	readonly Whiteboard: 'whiteboard';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Calendar: 'calendar';
	readonly SpaceCalendar: 'space-calendar';
	readonly Question: 'question';
	readonly Answer: 'answer';
	readonly Issue: 'issue';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Filter: 'filter';
	readonly Plan: 'plan';
	readonly Board: 'board';
	readonly Component: 'component';
	readonly Card: 'card';
	readonly Goal: 'goal';
	readonly Tag: 'tag';
	readonly WorkItem: 'work-item';
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
	readonly Gliffy: 'gliffy';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
	readonly DataTable: 'data-table';
	readonly AssetsObject: 'object';
	readonly Type: 'type';
	readonly Schema: 'schema';
	readonly Position: 'position';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Image: 'image';
	readonly Audio: 'audio';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Other: 'other';
	readonly Notebook: 'notebook';
	readonly Form: 'form';
} = contentTypeSchema.enum;

export const pageStatusSchema: z.ZodNativeEnum<{
	All: string;
	RoughDraft: 'roughDraft';
	InProgress: 'inProgress';
	ReadyForReview: 'readyForReview';
	Verified: 'verified';
}> = z.nativeEnum({
	...PageStatusFilterValueKey,
	...AllContentType,
});

export const lastModifiedValueSchema: z.ZodNativeEnum<{
	readonly Anytime: 'anytime';
	readonly Today: 'today';
	readonly Yesterday: 'yesterday';
	readonly Past7Days: 'past7Days';
	readonly Past30Days: 'past30Days';
	readonly PastYear: 'pastYear';
	readonly Custom: 'custom';
}> = z.nativeEnum({
	Anytime: 'anytime',
	Today: 'today',
	Yesterday: 'yesterday',
	Past7Days: 'past7Days',
	Past30Days: 'past30Days',
	PastYear: 'pastYear',
	Custom: 'custom',
} as const);
export const LastModified: {
	readonly Anytime: 'anytime';
	readonly Today: 'today';
	readonly Yesterday: 'yesterday';
	readonly Past7Days: 'past7Days';
	readonly Past30Days: 'past30Days';
	readonly PastYear: 'pastYear';
	readonly Custom: 'custom';
} = lastModifiedValueSchema.enum;
export type LastModifiedValue = z.infer<typeof lastModifiedValueSchema>;

export const sortSchema: z.ZodNativeEnum<{
	readonly Relevance: 'relevance';
	readonly UpdatedNewest: 'updatedNewest';
	readonly UpdatedOldest: 'updatedOldest';
	readonly CreatedNewest: 'createdNewest';
	readonly CreatedOldest: 'createdOldest';
}> = z.nativeEnum({
	Relevance: 'relevance',
	UpdatedNewest: 'updatedNewest',
	UpdatedOldest: 'updatedOldest',
	CreatedNewest: 'createdNewest',
	CreatedOldest: 'createdOldest',
} as const);
export const Sort: {
	readonly Relevance: 'relevance';
	readonly UpdatedNewest: 'updatedNewest';
	readonly UpdatedOldest: 'updatedOldest';
	readonly CreatedNewest: 'createdNewest';
	readonly CreatedOldest: 'createdOldest';
} = sortSchema.enum;
export type SortValue = z.infer<typeof sortSchema>;

/**
 * Backend experiment override format accepted via URL overrides.
 */
export type BackendExperimentOverride = Pick<SearchExperimentLayer, 'analyticsDefinitions'> & {
	name:
		| 'search_platform_layer_l1'
		| 'search_platform_layer_l2'
		| 'search_platform_layer_l3'
		| 'search_platform_layer_query_expansion';
};

export type Overrides = {
	sourceProduct?: string;
	frontendFeatureGates?: Array<{
		name: string;
		value: boolean;
	}>;
	frontendExperiments?: Array<{
		name: string;
		value: Record<string, string | boolean>;
	}>;
	backendExperiments?: BackendExperimentOverride[];
};

const jsonEncodedSchema = <T extends z.ZodTypeAny>(schema: T) =>
	z.preprocess((value) => {
		// Expected format: URL encoded JSON string
		if (typeof value === 'string') {
			let decoded = value;
			try {
				decoded = decodeURIComponent(value);
			} catch {
				// ignore decoding errors; schema will fail if JSON is invalid
			}
			try {
				return JSON.parse(decoded);
			} catch {
				return value;
			}
		}
		return value;
	}, schema);

export const overridesSchema = jsonEncodedSchema(
	z
		.object({
			sourceProduct: z.string().min(1).optional(),
			frontendFeatureGates: z
				.array(
					z.object({
						name: z.string().min(1),
						value: z.boolean(),
					}),
				)
				.optional(),
			frontendExperiments: z
				.array(
					z.object({
						name: z.string().min(1),
						value: z.record(z.union([z.string(), z.boolean()])),
					}),
				)
				.optional(),
			backendExperiments: z
				.array(
					z
						.object({
							name: z.enum([
								'search_platform_layer_l1',
								'search_platform_layer_l2',
								'search_platform_layer_l3',
								'search_platform_layer_query_expansion',
							]),
							analyticsDefinitions: z
								.array(
									z
										.object({
											layerId: z.string().nullable().optional(),
											shadowId: z.string().nullable().optional(),
											entity: z.string().nullable().optional(),
											subEntity: z.string().nullable().optional(),
											abTestId: z.string().nullable(),
											controlId: z.string().nullable(),
										})
										.passthrough(),
								)
								.optional(),
						})
						.passthrough(),
				)
				.optional(),
		})
		.passthrough(),
);

export const searchQueryParamsBaseSchema: z.ZodObject<
	{
		text: z.ZodOptional<z.ZodString>;
		originalQuery: z.ZodOptional<z.ZodString>;
		product: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodUnion<
					[
						z.ZodNativeEnum<{
							readonly AtlassianWebCrawler: 'atlassian-web-crawler';
							readonly Figma: 'figma';
							readonly Github: 'github';
							readonly GoogleDrive: 'drive';
							readonly Loom: 'loom';
							readonly OneDrive: 'onedrive';
							readonly Dropbox: 'dropbox';
							readonly Slack: 'slack';
							readonly Sharepoint: 'sharepoint';
							readonly Teams: 'teams';
							readonly GoogleCalendar: 'google-calendar';
							readonly OutlookCalendar: 'outlook-calendar';
							readonly OutlookMail: 'outlook-mail';
							readonly Gmail: 'gmail';
							readonly AdobeSign: 'adobe-sign';
							readonly Asana: 'asana';
							readonly Zendesk: 'zendesk';
							readonly Airtable: 'airtable';
							readonly Smartsheet: 'smartsheet';
							readonly Notion: 'notion';
							readonly Miro: 'miro';
							readonly Docusign: 'docusign';
							readonly Lucid: 'lucid';
							readonly Monday: 'monday';
							readonly AzureDevops: 'azure-devops';
							readonly Gitlab: 'gitlab';
							readonly ConfluenceDC: 'confluence-dc';
							readonly Canva: 'canva';
							readonly Box: 'box';
							readonly Zeplin: 'zeplin';
							readonly ServiceNow: 'servicenow';
							readonly Freshservice: 'freshservice';
							readonly PowerBI: 'power-bi';
							readonly ClickUp: 'clickup';
							readonly Amplitude: 'amplitude';
							readonly Stripe: 'stripe';
							readonly AdobeXD: 'adobexd';
							readonly Aha: 'aha';
							readonly Mural: 'mural';
							readonly Webex: 'webex';
							readonly Todoist: 'todoist';
							readonly PagerDuty: 'pagerduty';
							readonly Pipedrive: 'pipedrive';
							readonly Hubspot: 'hubspot';
							readonly Dovetail: 'dovetail';
							readonly LaunchDarkly: 'launchdarkly';
							readonly Salesforce: 'salesforce';
							readonly Sentry: 'sentry';
							readonly JiraDC: 'jira-dc';
							readonly Gitbook: 'gitbook';
							readonly AWSS3: 'aws-s3';
							readonly Databricks: 'databricks';
							readonly JsmDC: 'jsm-dc';
							readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
							readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
							readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
							readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
							readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
							readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
							readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
							readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
							readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
							readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
							readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
							readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
							readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
							readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
							readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
							readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
							readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
							readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
							readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
							readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
							readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
							readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
							readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
							readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
							readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
							readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
							readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
							readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
							readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
							readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
							readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
							readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
							readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
							readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
							readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
							readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
							readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
							readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
							readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
							readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
							readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
							readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
							readonly Atlas: 'townsquare';
							readonly Bitbucket: 'bitbucket';
							readonly Compass: 'compass';
							readonly Confluence: 'confluence';
							readonly Jira: 'jira';
							readonly JiraProductDiscovery: 'jira-product-discovery';
							readonly JiraServiceManagement: 'jira-servicedesk';
							readonly Trello: 'trello';
							readonly Focus: 'mercury';
							readonly Assets: 'cmdb';
							readonly Talent: 'radar';
						}>,
						z.ZodEffects<z.ZodString, string, string>,
					]
				>
			>
		>;
		datasource: z.ZodOptional<z.ZodString>;
		type: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					readonly All: string;
					readonly Document: 'document';
					readonly Spreadsheet: 'spreadsheet';
					readonly Presentation: 'presentation';
					readonly Repository: 'repository';
					readonly PullRequest: 'pull-request';
					readonly Branch: 'branch';
					readonly Commit: 'commit';
					readonly WebPage: 'web-page';
					readonly Conversation: 'conversation';
					readonly Message: 'message';
					readonly Video: 'video';
					readonly Design: 'design';
					readonly Page: 'page';
					readonly Blogpost: 'blogpost';
					readonly Attachment: 'attachment';
					readonly Space: 'space';
					readonly Whiteboard: 'whiteboard';
					readonly Comment: 'comment';
					readonly Database: 'database';
					readonly Embed: 'embed';
					readonly Folder: 'folder';
					readonly Calendar: 'calendar';
					readonly SpaceCalendar: 'space-calendar';
					readonly Question: 'question';
					readonly Answer: 'answer';
					readonly Issue: 'issue';
					readonly Epic: 'epic';
					readonly Bug: 'bug';
					readonly Story: 'story';
					readonly Task: 'task';
					readonly Subtask: 'subtask';
					readonly Project: 'project';
					readonly Incident: 'incident';
					readonly Filter: 'filter';
					readonly Plan: 'plan';
					readonly Board: 'board';
					readonly Component: 'component';
					readonly Card: 'card';
					readonly Goal: 'goal';
					readonly Tag: 'tag';
					readonly WorkItem: 'work-item';
					readonly FocusArea: 'focus-area';
					readonly Ask: 'ask';
					readonly Dashboard: 'dashboard';
					readonly Invoice: 'invoice';
					readonly Deal: 'deal';
					readonly Gliffy: 'gliffy';
					readonly CustomerContact: 'customer-contact';
					readonly CustomerOrg: 'customer-org';
					readonly DataTable: 'data-table';
					readonly AssetsObject: 'object';
					readonly Type: 'type';
					readonly Schema: 'schema';
					readonly Position: 'position';
					readonly MeetingTranscripts: 'meeting-transcripts';
					readonly Image: 'image';
					readonly Audio: 'audio';
					readonly Pdf: 'pdf';
					readonly Shortcut: 'shortcut';
					readonly Code: 'code';
					readonly Archive: 'archive';
					readonly Other: 'other';
					readonly Notebook: 'notebook';
					readonly Form: 'form';
				}>
			>
		>;
		filterDropdownOpen: z.ZodOptional<
			z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>
		>;
		lastModified: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					readonly Anytime: 'anytime';
					readonly Today: 'today';
					readonly Yesterday: 'yesterday';
					readonly Past7Days: 'past7Days';
					readonly Past30Days: 'past30Days';
					readonly PastYear: 'pastYear';
					readonly Custom: 'custom';
				}>
			>
		>;
		lastModified_lt: z.ZodOptional<z.ZodPipeline<z.ZodString, z.ZodDate>>;
		lastModified_gt: z.ZodOptional<z.ZodPipeline<z.ZodString, z.ZodDate>>;
		sort: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					readonly Relevance: 'relevance';
					readonly UpdatedNewest: 'updatedNewest';
					readonly UpdatedOldest: 'updatedOldest';
					readonly CreatedNewest: 'createdNewest';
					readonly CreatedOldest: 'createdOldest';
				}>
			>
		>;
		from: z.ZodOptional<z.ZodString>;
		to: z.ZodOptional<z.ZodString>;
		titleMatch: z.ZodOptional<
			z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>
		>;
		contributors: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		owners: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		ancestor: z.ZodOptional<z.ZodString>;
		salesforceStages: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		spaces: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		verified: z.ZodOptional<z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>>;
		labels: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		pageStatus: z.ZodOptional<
			z.ZodPipeline<
				z.ZodString,
				z.ZodNativeEnum<{
					All: string;
					RoughDraft: 'roughDraft';
					InProgress: 'inProgress';
					ReadyForReview: 'readyForReview';
					Verified: 'verified';
				}>
			>
		>;
		archived: z.ZodOptional<z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>>;
		jAssignee: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		jReporter: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		jCommentedByMe: z.ZodOptional<
			z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>
		>;
		jProject: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		jWatchedByMe: z.ZodOptional<
			z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>
		>;
		assetsSchema: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		assetsType: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		focusAreaTypes: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		focusAreaUnder: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		positionEmploymentTypes: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		positionFocusAreas: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		positionJobFamilies: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		positionStatuses: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		positionTeams: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		compassArchived: z.ZodOptional<
			z.ZodEffects<z.ZodEnum<['true', 'false']>, boolean, 'true' | 'false'>
		>;
		createdBy: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		lastUpdatedBy: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		closeDate: z.ZodOptional<z.ZodString>;
		closeDateVal: z.ZodOptional<z.ZodString>;
		opportunityStatus: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		referrer: z.ZodOptional<z.ZodString>;
		overrides: z.ZodOptional<z.ZodPipeline<z.ZodString, typeof overridesSchema>>;
		cql: z.ZodOptional<z.ZodString>;
		status: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
		isAutoApplied: z.ZodOptional<z.ZodEffects<z.ZodString, string[], string>>;
	},
	'strip',
	z.ZodTypeAny,
	{
		cql?: string | undefined;
		archived?: boolean | undefined;
		createdBy?: string[] | undefined;
		verified?: boolean | undefined;
		focusAreaUnder?: string[] | undefined;
		pageStatus?: string | undefined;
		lastUpdatedBy?: string[] | undefined;
		closeDate?: string | undefined;
		closeDateVal?: string | undefined;
		opportunityStatus?: string[] | undefined;
		compassArchived?: boolean | undefined;
		assetsSchema?: string[] | undefined;
		assetsType?: string[] | undefined;
		type?: string | undefined;
		text?: string | undefined;
		from?: string | undefined;
		to?: string | undefined;
		status?: string[] | undefined;
		spaces?: string[] | undefined;
		product?: string | undefined;
		originalQuery?: string | undefined;
		datasource?: string | undefined;
		filterDropdownOpen?: boolean | undefined;
		lastModified?:
			| 'custom'
			| 'anytime'
			| 'today'
			| 'yesterday'
			| 'past7Days'
			| 'past30Days'
			| 'pastYear'
			| undefined;
		lastModified_lt?: Date | undefined;
		lastModified_gt?: Date | undefined;
		sort?:
			| 'relevance'
			| 'updatedNewest'
			| 'updatedOldest'
			| 'createdNewest'
			| 'createdOldest'
			| undefined;
		titleMatch?: boolean | undefined;
		contributors?: string[] | undefined;
		owners?: string[] | undefined;
		ancestor?: string | undefined;
		salesforceStages?: string[] | undefined;
		labels?: string[] | undefined;
		jAssignee?: string[] | undefined;
		jReporter?: string[] | undefined;
		jCommentedByMe?: boolean | undefined;
		jProject?: string[] | undefined;
		jWatchedByMe?: boolean | undefined;
		focusAreaTypes?: string[] | undefined;
		positionEmploymentTypes?: string[] | undefined;
		positionFocusAreas?: string[] | undefined;
		positionJobFamilies?: string[] | undefined;
		positionStatuses?: string[] | undefined;
		positionTeams?: string[] | undefined;
		referrer?: string | undefined;
		isAutoApplied?: string[] | undefined;
		overrides?: Overrides;
	},
	{
		cql?: string | undefined;
		archived?: 'true' | 'false' | undefined;
		createdBy?: string | undefined;
		verified?: 'true' | 'false' | undefined;
		focusAreaUnder?: string | undefined;
		pageStatus?: string | undefined;
		lastUpdatedBy?: string | undefined;
		closeDate?: string | undefined;
		closeDateVal?: string | undefined;
		opportunityStatus?: string | undefined;
		compassArchived?: 'true' | 'false' | undefined;
		assetsSchema?: string | undefined;
		assetsType?: string | undefined;
		type?: string | undefined;
		text?: string | undefined;
		from?: string | undefined;
		to?: string | undefined;
		status?: string | undefined;
		spaces?: string | undefined;
		product?: string | undefined;
		originalQuery?: string | undefined;
		datasource?: string | undefined;
		filterDropdownOpen?: 'true' | 'false' | undefined;
		lastModified?: string | undefined;
		lastModified_lt?: string | undefined;
		lastModified_gt?: string | undefined;
		sort?: string | undefined;
		titleMatch?: 'true' | 'false' | undefined;
		contributors?: string | undefined;
		owners?: string | undefined;
		ancestor?: string | undefined;
		salesforceStages?: string | undefined;
		labels?: string | undefined;
		jAssignee?: string | undefined;
		jReporter?: string | undefined;
		jCommentedByMe?: 'true' | 'false' | undefined;
		jProject?: string | undefined;
		jWatchedByMe?: 'true' | 'false' | undefined;
		focusAreaTypes?: string | undefined;
		positionEmploymentTypes?: string | undefined;
		positionFocusAreas?: string | undefined;
		positionJobFamilies?: string | undefined;
		positionStatuses?: string | undefined;
		positionTeams?: string | undefined;
		referrer?: string | undefined;
		overrides?: string | undefined;
		isAutoApplied?: string | undefined;
	}
> = z.object({
	text: z.optional(z.string().max(200)), // TODO: >>>> Ask how long should be the max query string?
	originalQuery: z.optional(z.string().max(200)), // Used to support "Did you mean" feature
	product: z.optional(z.string().pipe(productKeySchema)),
	datasource: z.optional(z.string()),
	type: z.optional(z.string().pipe(contentTypeSchema)),
	filterDropdownOpen: z.optional(booleanSchema),

	// Last Modified
	lastModified: z.optional(z.string().pipe(lastModifiedValueSchema)),
	lastModified_lt: z.optional(z.string().pipe(z.coerce.date())),
	lastModified_gt: z.optional(z.string().pipe(z.coerce.date())),
	from: z.optional(z.string()),
	to: z.optional(z.string()),

	sort: z.optional(z.string().pipe(sortSchema)),

	titleMatch: z.optional(booleanSchema),

	// Confluence filters
	contributors: z.optional(commaSeparatedArraySchema),
	owners: z.optional(commaSeparatedArraySchema),
	ancestor: z.optional(z.string()),

	// Salesforce Filters
	salesforceStages: z.optional(commaSeparatedArraySchema),
	spaces: z.optional(commaSeparatedArraySchema),
	verified: z.optional(booleanSchema),
	labels: z.optional(commaSeparatedArraySchema),
	pageStatus: z.optional(z.string().pipe(pageStatusSchema)),
	archived: z.optional(booleanSchema),

	// Jira Filters
	jAssignee: z.optional(commaSeparatedArraySchema),
	jReporter: z.optional(commaSeparatedArraySchema),
	jCommentedByMe: z.optional(booleanSchema),
	jProject: z.optional(commaSeparatedArraySchema),
	jWatchedByMe: z.optional(booleanSchema),

	// Assets Filters
	assetsSchema: z.optional(commaSeparatedArraySchema),
	assetsType: z.optional(commaSeparatedArraySchema),

	// Mercury Filters
	focusAreaTypes: z.optional(commaSeparatedArraySchema),
	focusAreaUnder: z.optional(commaSeparatedArraySchema),

	// Talent Filters
	positionEmploymentTypes: z.optional(commaSeparatedArraySchema),
	positionFocusAreas: z.optional(commaSeparatedArraySchema),
	positionJobFamilies: z.optional(commaSeparatedArraySchema),
	positionStatuses: z.optional(commaSeparatedArraySchema),
	positionTeams: z.optional(commaSeparatedArraySchema),

	// Compass Filters
	compassArchived: z.optional(booleanSchema),

	createdBy: z.optional(commaSeparatedArraySchema),

	lastUpdatedBy: z.optional(commaSeparatedArraySchema),

	// Salesforce Close Date filter
	closeDate: z.optional(z.string()),
	closeDateVal: z.optional(z.string()),

	// Salesforce Opportunity Status filter
	opportunityStatus: z.optional(commaSeparatedArraySchema),

	referrer: z.optional(z.string()),

	/**
	 * URL encoded JSON string used to override feature gates/experiments for debugging.
	 */
	overrides: z.optional(z.string().pipe(overridesSchema)),

	cql: z.optional(z.string()),

	status: z.optional(commaSeparatedArraySchema),

	isAutoApplied: z.optional(commaSeparatedArraySchema),
});

export type SearchQueryStringParams = z.infer<typeof searchQueryParamsBaseSchema>;
export const SearchQueryStringParamKey: z.Values<
	[
		'cql',
		'archived',
		'createdBy',
		'verified',
		'focusAreaUnder',
		'pageStatus',
		'lastUpdatedBy',
		'compassArchived',
		'assetsSchema',
		'assetsType',
		'type',
		'text',
		'from',
		'to',
		'status',
		'spaces',
		'product',
		'originalQuery',
		'datasource',
		'filterDropdownOpen',
		'sort',
		'lastModified',
		'lastModified_lt',
		'lastModified_gt',
		'titleMatch',
		'contributors',
		'owners',
		'ancestor',
		'salesforceStages',
		'labels',
		'jAssignee',
		'jReporter',
		'jCommentedByMe',
		'jProject',
		'jWatchedByMe',
		'focusAreaTypes',
		'positionEmploymentTypes',
		'positionFocusAreas',
		'positionJobFamilies',
		'positionStatuses',
		'positionTeams',
		'closeDate',
		'closeDateVal',
		'opportunityStatus',
		'referrer',
		'isAutoApplied',
		'overrides',
	]
> = searchQueryParamsBaseSchema.keyof().enum;
export type SearchQueryStringParamKeys = keyof SearchQueryStringParams;
export type SearchQueryStringParamsRaw = z.input<typeof searchQueryParamsBaseSchema>;
type ExtractKeysWithArrayValues<T> = {
	[P in keyof T]: T[P] extends unknown[] ? P : never;
}[keyof T];
export type SearchQueryStringParamKeysWithValues = NonNullable<
	ExtractKeysWithArrayValues<SearchQueryStringParams>
>;

export const searchQueryParamsValidators: ((
	queryParams: SearchQueryStringParams,
) => SearchQueryStringParams)[] = [
	(queryParams) => {
		if (queryParams.lastModified !== 'custom') {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { lastModified_gt, lastModified_lt, ...restQueryParams } = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
	(queryParams) => {
		const { lastModified_lt, lastModified_gt } = queryParams;
		if (lastModified_lt && lastModified_gt && lastModified_lt > lastModified_gt) {
			const {
				lastModified: _,
				lastModified_gt: __,
				lastModified_lt: ___,
				...restQueryParams
			} = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
	// If closeDate is not 'custom', strip out closeDateVal
	(queryParams) => {
		if (queryParams.closeDate !== 'custom') {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { closeDateVal, ...restQueryParams } = queryParams;
			return restQueryParams;
		}
		return queryParams;
	},
] satisfies ((queryParams: SearchQueryStringParams) => SearchQueryStringParams)[];

export const filterKeysSchema: z.ZodNativeEnum<{
	readonly cql: 'cql';
	readonly archived: 'archived';
	readonly createdBy: 'createdBy';
	readonly verified: 'verified';
	readonly focusAreaUnder: 'focusAreaUnder';
	readonly pageStatus: 'pageStatus';
	readonly lastUpdatedBy: 'lastUpdatedBy';
	readonly compassArchived: 'compassArchived';
	readonly assetsSchema: 'assetsSchema';
	readonly assetsType: 'assetsType';
	readonly type: 'type';
	readonly text: 'text';
	readonly from: 'from';
	readonly to: 'to';
	readonly status: 'status';
	readonly spaces: 'spaces';
	readonly product: 'product';
	readonly originalQuery: 'originalQuery';
	readonly datasource: 'datasource';
	readonly filterDropdownOpen: 'filterDropdownOpen';
	readonly lastModified: 'lastModified';
	readonly lastModified_lt: 'lastModified_lt';
	readonly lastModified_gt: 'lastModified_gt';
	readonly sort: 'sort';
	readonly titleMatch: 'titleMatch';
	readonly contributors: 'contributors';
	readonly owners: 'owners';
	readonly ancestor: 'ancestor';
	readonly salesforceStages: 'salesforceStages';
	readonly labels: 'labels';
	readonly jAssignee: 'jAssignee';
	readonly jReporter: 'jReporter';
	readonly jCommentedByMe: 'jCommentedByMe';
	readonly jProject: 'jProject';
	readonly jWatchedByMe: 'jWatchedByMe';
	readonly focusAreaTypes: 'focusAreaTypes';
	readonly positionEmploymentTypes: 'positionEmploymentTypes';
	readonly positionFocusAreas: 'positionFocusAreas';
	readonly positionJobFamilies: 'positionJobFamilies';
	readonly positionStatuses: 'positionStatuses';
	readonly positionTeams: 'positionTeams';
	readonly closeDate: 'closeDate';
	readonly closeDateVal: 'closeDateVal';
	readonly opportunityStatus: 'opportunityStatus';
	readonly referrer: 'referrer';
	readonly overrides: 'overrides';
	readonly isAutoApplied: 'isAutoApplied';
	readonly Anytime: 'anytime';
	readonly Today: 'today';
	readonly Yesterday: 'yesterday';
	readonly Past7Days: 'past7Days';
	readonly Past30Days: 'past30Days';
	readonly PastYear: 'pastYear';
	readonly Custom: 'custom';
	readonly All: string;
	readonly Document: 'document';
	readonly Spreadsheet: 'spreadsheet';
	readonly Presentation: 'presentation';
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly WebPage: 'web-page';
	readonly Conversation: 'conversation';
	readonly Message: 'message';
	readonly Video: 'video';
	readonly Design: 'design';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Attachment: 'attachment';
	readonly Space: 'space';
	readonly Whiteboard: 'whiteboard';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Calendar: 'calendar';
	readonly SpaceCalendar: 'space-calendar';
	readonly Question: 'question';
	readonly Answer: 'answer';
	readonly Issue: 'issue';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Filter: 'filter';
	readonly Plan: 'plan';
	readonly Board: 'board';
	readonly Component: 'component';
	readonly Card: 'card';
	readonly Goal: 'goal';
	readonly Tag: 'tag';
	readonly WorkItem: 'work-item';
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
	readonly Gliffy: 'gliffy';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
	readonly DataTable: 'data-table';
	readonly AssetsObject: 'object';
	readonly Type: 'type';
	readonly Schema: 'schema';
	readonly Position: 'position';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Image: 'image';
	readonly Audio: 'audio';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Other: 'other';
	readonly Notebook: 'notebook';
	readonly Form: 'form';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly GoogleDrive: 'drive';
	readonly Loom: 'loom';
	readonly OneDrive: 'onedrive';
	readonly Dropbox: 'dropbox';
	readonly Slack: 'slack';
	readonly Sharepoint: 'sharepoint';
	readonly Teams: 'teams';
	readonly GoogleCalendar: 'google-calendar';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly OutlookMail: 'outlook-mail';
	readonly Gmail: 'gmail';
	readonly AdobeSign: 'adobe-sign';
	readonly Asana: 'asana';
	readonly Zendesk: 'zendesk';
	readonly Airtable: 'airtable';
	readonly Smartsheet: 'smartsheet';
	readonly Notion: 'notion';
	readonly Miro: 'miro';
	readonly Docusign: 'docusign';
	readonly Lucid: 'lucid';
	readonly Monday: 'monday';
	readonly AzureDevops: 'azure-devops';
	readonly Gitlab: 'gitlab';
	readonly ConfluenceDC: 'confluence-dc';
	readonly Canva: 'canva';
	readonly Box: 'box';
	readonly Zeplin: 'zeplin';
	readonly ServiceNow: 'servicenow';
	readonly Freshservice: 'freshservice';
	readonly PowerBI: 'power-bi';
	readonly ClickUp: 'clickup';
	readonly Amplitude: 'amplitude';
	readonly Stripe: 'stripe';
	readonly AdobeXD: 'adobexd';
	readonly Aha: 'aha';
	readonly Mural: 'mural';
	readonly Webex: 'webex';
	readonly Todoist: 'todoist';
	readonly PagerDuty: 'pagerduty';
	readonly Pipedrive: 'pipedrive';
	readonly Hubspot: 'hubspot';
	readonly Dovetail: 'dovetail';
	readonly LaunchDarkly: 'launchdarkly';
	readonly Salesforce: 'salesforce';
	readonly Sentry: 'sentry';
	readonly JiraDC: 'jira-dc';
	readonly Gitbook: 'gitbook';
	readonly AWSS3: 'aws-s3';
	readonly Databricks: 'databricks';
	readonly JsmDC: 'jsm-dc';
	readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
	readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
	readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
	readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
	readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
	readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
	readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
	readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
	readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
	readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
	readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
	readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
	readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
	readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
	readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
	readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
	readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
	readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
	readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
	readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
	readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
	readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
	readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
	readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
	readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
	readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
	readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
	readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
	readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
	readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
	readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
	readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
	readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
	readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
	readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
	readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
	readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
	readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
	readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
	readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
	readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
	readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
	readonly Atlas: 'townsquare';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Trello: 'trello';
	readonly Focus: 'mercury';
	readonly Assets: 'cmdb';
	readonly Talent: 'radar';
}> = z.nativeEnum({
	...ProductKey,
	...ContentType,
	...LastModified,
	...SearchQueryStringParamKey,
} as const);
export const FilterKey: {
	readonly cql: 'cql';
	readonly archived: 'archived';
	readonly createdBy: 'createdBy';
	readonly verified: 'verified';
	readonly focusAreaUnder: 'focusAreaUnder';
	readonly pageStatus: 'pageStatus';
	readonly lastUpdatedBy: 'lastUpdatedBy';
	readonly compassArchived: 'compassArchived';
	readonly assetsSchema: 'assetsSchema';
	readonly assetsType: 'assetsType';
	readonly type: 'type';
	readonly text: 'text';
	readonly from: 'from';
	readonly to: 'to';
	readonly status: 'status';
	readonly spaces: 'spaces';
	readonly product: 'product';
	readonly originalQuery: 'originalQuery';
	readonly datasource: 'datasource';
	readonly filterDropdownOpen: 'filterDropdownOpen';
	readonly lastModified: 'lastModified';
	readonly lastModified_lt: 'lastModified_lt';
	readonly lastModified_gt: 'lastModified_gt';
	readonly sort: 'sort';
	readonly titleMatch: 'titleMatch';
	readonly contributors: 'contributors';
	readonly owners: 'owners';
	readonly ancestor: 'ancestor';
	readonly salesforceStages: 'salesforceStages';
	readonly labels: 'labels';
	readonly jAssignee: 'jAssignee';
	readonly jReporter: 'jReporter';
	readonly jCommentedByMe: 'jCommentedByMe';
	readonly jProject: 'jProject';
	readonly jWatchedByMe: 'jWatchedByMe';
	readonly focusAreaTypes: 'focusAreaTypes';
	readonly positionEmploymentTypes: 'positionEmploymentTypes';
	readonly positionFocusAreas: 'positionFocusAreas';
	readonly positionJobFamilies: 'positionJobFamilies';
	readonly positionStatuses: 'positionStatuses';
	readonly positionTeams: 'positionTeams';
	readonly closeDate: 'closeDate';
	readonly closeDateVal: 'closeDateVal';
	readonly opportunityStatus: 'opportunityStatus';
	readonly referrer: 'referrer';
	readonly overrides: 'overrides';
	readonly isAutoApplied: 'isAutoApplied';
	readonly Anytime: 'anytime';
	readonly Today: 'today';
	readonly Yesterday: 'yesterday';
	readonly Past7Days: 'past7Days';
	readonly Past30Days: 'past30Days';
	readonly PastYear: 'pastYear';
	readonly Custom: 'custom';
	readonly All: string;
	readonly Document: 'document';
	readonly Spreadsheet: 'spreadsheet';
	readonly Presentation: 'presentation';
	readonly Repository: 'repository';
	readonly PullRequest: 'pull-request';
	readonly Branch: 'branch';
	readonly Commit: 'commit';
	readonly WebPage: 'web-page';
	readonly Conversation: 'conversation';
	readonly Message: 'message';
	readonly Video: 'video';
	readonly Design: 'design';
	readonly Page: 'page';
	readonly Blogpost: 'blogpost';
	readonly Attachment: 'attachment';
	readonly Space: 'space';
	readonly Whiteboard: 'whiteboard';
	readonly Comment: 'comment';
	readonly Database: 'database';
	readonly Embed: 'embed';
	readonly Folder: 'folder';
	readonly Calendar: 'calendar';
	readonly SpaceCalendar: 'space-calendar';
	readonly Question: 'question';
	readonly Answer: 'answer';
	readonly Issue: 'issue';
	readonly Epic: 'epic';
	readonly Bug: 'bug';
	readonly Story: 'story';
	readonly Task: 'task';
	readonly Subtask: 'subtask';
	readonly Project: 'project';
	readonly Incident: 'incident';
	readonly Filter: 'filter';
	readonly Plan: 'plan';
	readonly Board: 'board';
	readonly Component: 'component';
	readonly Card: 'card';
	readonly Goal: 'goal';
	readonly Tag: 'tag';
	readonly WorkItem: 'work-item';
	readonly FocusArea: 'focus-area';
	readonly Ask: 'ask';
	readonly Dashboard: 'dashboard';
	readonly Invoice: 'invoice';
	readonly Deal: 'deal';
	readonly Gliffy: 'gliffy';
	readonly CustomerContact: 'customer-contact';
	readonly CustomerOrg: 'customer-org';
	readonly DataTable: 'data-table';
	readonly AssetsObject: 'object';
	readonly Type: 'type';
	readonly Schema: 'schema';
	readonly Position: 'position';
	readonly MeetingTranscripts: 'meeting-transcripts';
	readonly Image: 'image';
	readonly Audio: 'audio';
	readonly Pdf: 'pdf';
	readonly Shortcut: 'shortcut';
	readonly Code: 'code';
	readonly Archive: 'archive';
	readonly Other: 'other';
	readonly Notebook: 'notebook';
	readonly Form: 'form';
	readonly AtlassianWebCrawler: 'atlassian-web-crawler';
	readonly Figma: 'figma';
	readonly Github: 'github';
	readonly GoogleDrive: 'drive';
	readonly Loom: 'loom';
	readonly OneDrive: 'onedrive';
	readonly Dropbox: 'dropbox';
	readonly Slack: 'slack';
	readonly Sharepoint: 'sharepoint';
	readonly Teams: 'teams';
	readonly GoogleCalendar: 'google-calendar';
	readonly OutlookCalendar: 'outlook-calendar';
	readonly OutlookMail: 'outlook-mail';
	readonly Gmail: 'gmail';
	readonly AdobeSign: 'adobe-sign';
	readonly Asana: 'asana';
	readonly Zendesk: 'zendesk';
	readonly Airtable: 'airtable';
	readonly Smartsheet: 'smartsheet';
	readonly Notion: 'notion';
	readonly Miro: 'miro';
	readonly Docusign: 'docusign';
	readonly Lucid: 'lucid';
	readonly Monday: 'monday';
	readonly AzureDevops: 'azure-devops';
	readonly Gitlab: 'gitlab';
	readonly ConfluenceDC: 'confluence-dc';
	readonly Canva: 'canva';
	readonly Box: 'box';
	readonly Zeplin: 'zeplin';
	readonly ServiceNow: 'servicenow';
	readonly Freshservice: 'freshservice';
	readonly PowerBI: 'power-bi';
	readonly ClickUp: 'clickup';
	readonly Amplitude: 'amplitude';
	readonly Stripe: 'stripe';
	readonly AdobeXD: 'adobexd';
	readonly Aha: 'aha';
	readonly Mural: 'mural';
	readonly Webex: 'webex';
	readonly Todoist: 'todoist';
	readonly PagerDuty: 'pagerduty';
	readonly Pipedrive: 'pipedrive';
	readonly Hubspot: 'hubspot';
	readonly Dovetail: 'dovetail';
	readonly LaunchDarkly: 'launchdarkly';
	readonly Salesforce: 'salesforce';
	readonly Sentry: 'sentry';
	readonly JiraDC: 'jira-dc';
	readonly Gitbook: 'gitbook';
	readonly AWSS3: 'aws-s3';
	readonly Databricks: 'databricks';
	readonly JsmDC: 'jsm-dc';
	readonly ForgeAdamVitareqDev: 'forge-connector-dAkghOClvWGokDftTTaIgNoD6pnNt74z-vitareq-connector';
	readonly ForgeAdamVitareqProd: 'forge-connector-EjfoGWrWrETl3Zc5zkGynPyESZO5Qu93-vitareq-connector';
	readonly ForgeK15tScrollSitesDev: 'forge-connector-h1n3NRFAn84v7ECSONVidlCS88qZh8cy-scroll-sites-connector';
	readonly ForgeK15tScrollSitesProd: 'forge-connector-ofe9zXccOCQ3U4t4kDVywSyJUoZjb1sO-scroll-sites-connector';
	readonly ForgePioAwsDev: 'forge-connector-HaxJprV0zzjjvwLhGkAN2gDPhHZwXNiL-s3-connector';
	readonly ForgePioAwsProd: 'forge-connector-mRcqWRwqkUd4JwapGkvGHUViyySX9MVy-s3-connector';
	readonly ForgePioClickupDev: 'forge-connector-ELuz3hTMhig0Jdrr6ytI45SifO9DfKrY-clickup-connector';
	readonly ForgePioClickupProd: 'forge-connector-uwYqtNjM6w0QIhHOFG0Cq4cnkUM9mt3K-clickup-connector';
	readonly ForgePioGitbookDev: 'forge-connector-Xh70v2yqkzOjTQqTMLxk1lElehWIXoYi-gitbook-connector';
	readonly ForgePioGitbookProd: 'forge-connector-cQ1IDSmJT0DrQuF8pGa4ijz0XZ6IGF5u-gitbook-connector';
	readonly ForgePioGithubDev: 'forge-connector-3QYYBgeNDilQB3Jc7uGMCchNgUGvCi7j-github-connector';
	readonly ForgePioGithubProd: 'forge-connector-0Sw5Yr860fgfx5sYS7kEUoxnGhEvrhhI-github-connector';
	readonly ForgePioGitlabDev: 'forge-connector-psr1wBkasRuKCwsvwi4aTJNhjmlVFebC-gitlab-connector';
	readonly ForgePioGitlabProd: 'forge-connector-QLK5Xf8nBqejSZTulw5s3Cl6fIS24MwL-gitlab-connector';
	readonly ForgePioGoogleDriveDev: 'forge-connector-JhqjCcAP6AHHxkYhY2akTgZsYXY93SMq-google-drive-connector';
	readonly ForgePioGoogleDriveProd: 'forge-connector-pcIwW04B398CSnilRIyzPJ5v5GevlCCo-google-drive-connector';
	readonly ForgePioGuruDev: 'forge-connector-NtY3W5Z8JTBshAgPZFIfT7VjYAARj1P5-guru-connector';
	readonly ForgePioGuruProd: 'forge-connector-mulxOVrXWOZObhFZH5WAqs7Ga2lCPK29-guru-connector';
	readonly ForgePioHelpjuiceDev: 'forge-connector-f1rvRuAyoDWIwDs7Xubi5AZJTuESM5PC-helpjuice-connector';
	readonly ForgePioHelpjuiceProd: 'forge-connector-8durapLyclxZeEexKkwxw6ZpCRpBz4dP-helpjuice-connector';
	readonly ForgePioHubspotDev: 'forge-connector-RtvzhBL0FFFIjLCBHjXI8QfgDqAs79WF-hubspot-connector';
	readonly ForgePioHubspotProd: 'forge-connector-O19vyTOCxgkZ6KSHycRjpzC0543x6Z0J-hubspot-connector';
	readonly ForgePioSalesforceDev: 'forge-connector-50BYkfgS4GXtr6iO8qUeBFbbHHbuFleo-salesforce-connector';
	readonly ForgePioSalesforceProd: 'forge-connector-nn5lJU1KJxJsUNML5GWV3blDYHMT6nAb-salesforce-connector';
	readonly ForgePioSharepointDev: 'forge-connector-cl5i3J8L463hTuQiDt7f1mK4vZllsAo0-sharepoint-connector';
	readonly ForgePioSharepointProd: 'forge-connector-7yrzYPlAOcZAif2tIymeDXBUNUAxI95G-sharepoint-connector';
	readonly ForgePioStackOverflowDev: 'forge-connector-ojk0u9oXGmVmS3BC7VmVSBPPIuuF5df3-stack-overflow-connector';
	readonly ForgePioStackOverflowProd: 'forge-connector-vUmPXguTcgLzdG1R3H0kTUQwxoGnZcsS-stack-overflow-connector';
	readonly ForgeUtoolityGitbookDev: 'forge-connector-FEJ0dhTb8HC3UYmqhPAKYCWVTcpoHsp5-gitbook-connector';
	readonly ForgeUtoolityGitbookProd: 'forge-connector-byNN1wfDuTnx7VndySG7Qq3ZfoXudUcL-gitbook-connector';
	readonly ForgeGdriveEugeneTestDev: 'forge-connector-s7M8FnmJVPQh9FtRqvqwSL3QFtqMijua-gdrive';
	readonly ForgeGdriveEugeneTestProd: 'forge-connector-bkRlqLiPfviKrVtbA6Q0QGN22x6b37mf-gdrive';
	readonly ForgeUnitoSalesforceDev: 'forge-connector-h8rZu86YJHRfyVlmVeUR464Kuhz9OZvV-salesforce';
	readonly ForgeUnitoSalesforceProd: 'forge-connector-qjk3Z8Kt2DWx29hdZHQapEYXAGCPIyd8-salesforce';
	readonly ForgeUnitoServicenowDev: 'forge-connector-52qcnF9TIgxkDKY4OL7xNxAzcM0amb7q-servicenow';
	readonly ForgeUnitoServicenowProd: 'forge-connector-RzFogW4EQLcXYmYpKkItRUhLAn3jETMq-servicenow';
	readonly ForgeUnitoWrikeDev: 'forge-connector-gw0rFhQkj7PuTllmYdHbMaPw6jC18YvO-wrike';
	readonly ForgeUnitoWrikeProd: 'forge-connector-390XhSnH4YgwqVgmyqYOujxVoL3IOUcM-wrike';
	readonly ForgeUnitoAsanaDev: 'forge-connector-6wHDDwNbam77WvGwMKYMGDiTWP8pza54-asana';
	readonly ForgeUnitoAsanaProd: 'forge-connector-sFXfxz3XEDS1wAWWCe345UVrSbUm9zyb-asana';
	readonly ForgeUnitoBasecampDev: 'forge-connector-nImlCTNLZUCtQzFsthpnlYJL0B981lMH-basecamp';
	readonly ForgeUnitoBasecampProd: 'forge-connector-TZqLHQtzvKxRikkh1KQlp4acwCzS5W1Z-basecamp';
	readonly Atlas: 'townsquare';
	readonly Bitbucket: 'bitbucket';
	readonly Compass: 'compass';
	readonly Confluence: 'confluence';
	readonly Jira: 'jira';
	readonly JiraProductDiscovery: 'jira-product-discovery';
	readonly JiraServiceManagement: 'jira-servicedesk';
	readonly Trello: 'trello';
	readonly Focus: 'mercury';
	readonly Assets: 'cmdb';
	readonly Talent: 'radar';
} = filterKeysSchema.enum;
export type FilterKeys = z.infer<typeof filterKeysSchema>;
export type FilterKeysRaw = boolean | readonly string[];
